
import { createSlice } from '@reduxjs/toolkit';
import { applyEdgeChanges, applyNodeChanges } from 'reactflow';
import { DEFAULT_WCA_BOTFLOW_STATE } from '../../../constants/defaultStates/botflow-wca';
import {
  addBotFlowSteps,
  addNewBotFlow,
  deleteBotflow,
  getAllBotFlow,
  getBotFlow,
  updateBotFlow,
  updateBotflowDetails,
} from './botflow-action';

const getSchemaPayload = (payload) => payload?.draftSchema || payload || {};

const getSchemaNodes = (payload) => {
  const schemaPayload = getSchemaPayload(payload);
  return Array.isArray(schemaPayload?.nodes) ? schemaPayload.nodes : [];
};

const getSchemaEdges = (payload) => {
  const schemaPayload = getSchemaPayload(payload);
  return Array.isArray(schemaPayload?.edges) ? schemaPayload.edges : [];
};

const getSchemaRootStepId = (payload) => {
  const schemaPayload = getSchemaPayload(payload);
  return schemaPayload?.rootStepId ?? null;
};

const wcaBotFlowSlice = createSlice({
  name: 'wcaBotFlow',
  initialState: DEFAULT_WCA_BOTFLOW_STATE,
  reducers: {
    changeBotflowPage(state, { payload }) {
      state.page = payload;
    },
    changeBotflowLimit(state, { payload }) {
      state.limit = payload;
    },
    changeSelectectBotFlow(state, { payload }) {
      state.selectedBotFlowId = payload;
    },
    openConfigScreen(state, { payload }) {
      state.editNodeCache = payload;
      state.selectedNode = payload;
      state.configScreenOpen = true;
    },
    closeConfigScreen(state) {
      state.deletedOptionNodeEdgeCache = [];
      state.configScreenOpen = false;
      state.editNodeCache = null;
      state.updatedEdgeChache = [];
    },
    undoNodeData(state) {
      if (state.editNodeCache !== null) {
        state.nodes = state.nodes?.map((node) => {
          if (node.id == state.editNodeCache.id) return state.editNodeCache;
          return node;
        });

        if (['list', 'quick_reply'].includes(state.editNodeCache?.type)) {
          const unchangesEdges = [];

          for (const edge of state.edges) {
            const isInUpdatedEdgeCache = state.updatedEdgeChache.find(
              (optEdge) => optEdge?.id == edge?.id,
            );

            if (isInUpdatedEdgeCache) {
              unchangesEdges.push(isInUpdatedEdgeCache);
            } else {
              unchangesEdges.push(edge);
            }
          }

          state.edges = [...unchangesEdges, ...state.deletedOptionNodeEdgeCache];
        }
      }
    },
    setNodes(state, { payload }) {
      state.nodes = applyNodeChanges(payload, state.nodes);
    },
    setEdges(state, action) {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    addNodeToStore(state, { payload }) {
      state.nodes = [...state.nodes, payload];
    },
    addEdgeToStore(state, action) {
      return {
        ...state,
        edges: [...state.edges, action.payload],
      };
    },
    changeSelectedNode(state, action) {
      state.selectedNode = action.payload;
    },
    changeQuickReplyNode(state, action) {
      state.quickReplyNode = action.payload;
    },
    updateTextNode(state, action) {
      const { nodeId, label } = action.payload;
      const nodeToUpdate = state.nodes.find((node) => node.id === nodeId);

      if (nodeToUpdate) {
        nodeToUpdate.data = {
          ...nodeToUpdate.data,
          label,
        };
      }
    },
    deleteNode(state, action) {
      const { nodeId } = action.payload;
      state.nodes = state.nodes?.filter((node) => node.id !== nodeId);

      if (state.nodes.length === 0) state.rootStepId = null;
    },
    deleteConnectedNodes(state, { payload }) {
      const deleteNode = payload.deleteNode;

      if (deleteNode.id === state.rootStepId) {
        const edgeConnectedToRoot = state.edges.find((edge) => edge.source === state.rootStepId);
        state.rootStepId = edgeConnectedToRoot?.target;
      }

      const sourceNodeIdList = [];
      const newEdgesList = [];

      const currentEdges = [...state.edges];
      for (const edge of currentEdges) {
        if (edge.target == deleteNode.id) {
          sourceNodeIdList.push(edge.source);
        }

        if (edge.source != deleteNode.id && edge.target != deleteNode.id) {
          newEdgesList.push(edge);
        }
      }

      const updatedInteractiveNodes = {};

      for (const nodeId of sourceNodeIdList) {
        let sourceNode = state.nodes.find((node) => node.id == nodeId);
        if (['quick_reply', 'list', 'template'].includes(sourceNode.type)) {
          sourceNode = {
            ...sourceNode,
            data: {
              ...sourceNode.data,
              options: sourceNode.data?.options?.map((opt) => {
                const postbackId = opt.postbackText.split('=')[1] || '';
                if (postbackId == deleteNode.id) {
                  opt.postbackText = '';
                  return opt;
                }
                return opt;
              }),
            },
          };
          updatedInteractiveNodes[sourceNode.id] = sourceNode;
        }
      }

      const newNodeList = [];
      for (const node of state.nodes) {
        if (node.id != deleteNode.id) {
          if (Object.keys(updatedInteractiveNodes)?.includes(node.id)) {
            newNodeList.push(updatedInteractiveNodes[node.id]);
          } else {
            newNodeList.push(node);
          }
        }
      }
      state.nodes = newNodeList;
      state.edges = newEdgesList;

      state.selectedNode = null;
      state.editNodeCache = null;
      state.configScreenOpen = false;
      if (newNodeList.length === 0) state.rootStepId = null;
    },
    deleteEdge(state, action) {
      const { edgeId } = action.payload;
      const deletedEdge = state.edges.find((edge) => edge.id == edgeId);
      let updatedNode = null;

      const currentEdges = [...state.edges];
      const newEdges = currentEdges?.filter((edge) => edge.id !== edgeId);
      state.edges = [...newEdges];

      if (deletedEdge && deletedEdge?.optionId) {
        const { optionId, source, target } = deletedEdge;
        const currNodes = [...state.nodes];
        const updatedNodeList = currNodes?.map((node) => {
          if (node.id == source && ['quick_reply', 'list', 'template'].includes(node.type) && node.data?.options) {
            updatedNode = {
              ...node,
              data: {
                ...node.data,
                options: node.data.options?.map((opt) => {
                  if (
                    opt.id == optionId ||
                    (opt.postbackText && opt.postbackText.split('=')[1] == target)
                  ) {
                    return {
                      ...opt,
                      postbackText: '',
                    };
                  }
                  return opt;
                }),
              },
            };
            return updatedNode;
          }
          return node;
        });
        state.nodes = [...updatedNodeList];
        state.deletedOptionNodeEdgeCache = [...state.deletedOptionNodeEdgeCache, deletedEdge];
      }

      if (updatedNode?.id == state.selectedNode?.id) {
        state.selectedNode = updatedNode;
      }
    },
    layoutNodes(state, action) {
      state.nodes = [...action.payload];
    },
    layoutEdges(state, action) {
      state.edges = [...action.payload];
    },
    changeRootStepId(state, action) {
      state.rootStepId = action.payload;
    },
    deleteNodeData(state, action) {
      const { nodeId, propertyToDelete } = action.payload;
      let updatedSelectedNode = state.selectedNode;

      if (updatedSelectedNode && updatedSelectedNode.id === nodeId) {
        const updatedData = { ...updatedSelectedNode.data };
        delete updatedData[propertyToDelete];

        updatedSelectedNode = {
          ...updatedSelectedNode,
          data: updatedData,
        };
      }

      const nodes = state.nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: Object.fromEntries(
                Object.entries(node.data).filter(([key]) => key !== propertyToDelete),
              ),
            }
          : node,
      );

      return {
        ...state,
        nodes,
        selectedNode: updatedSelectedNode,
      };
    },
    addNewOption(state, { payload }) {
      const { selectedNodeId, newOption } = payload;
      let updatedNode = null;

      state.nodes = state.nodes?.map((node) => {
        if (node.id == selectedNodeId) {
          updatedNode = {
            ...node,
            data: {
              ...node.data,
              options: [...node.data.options, newOption],
            },
          };
          return updatedNode;
        }
        return node;
      });

      if (updatedNode) {
        state.selectedNode = updatedNode;
      }
    },
    updateNodePayload(state, { payload }) {
      const { selectedNodeId, updatedData } = payload;
      state.selectedNode = {
        ...state.selectedNode,
        data: { ...state.selectedNode.data, ...updatedData },
      };

      state.nodes = state.nodes?.map((node) => {
        if (node.id == selectedNodeId) {
          return {
            ...node,
            data: { ...node.data, ...updatedData },
          };
        }
        return node;
      });
    },
    updateNodeData(state, action) {
      const { nodeId, updatedData } = action.payload;
      return {
        ...state,
        nodes: state.nodes.map((node) => (node.id === nodeId ? { ...node, ...updatedData } : node)),
      };
    },
    updateOptionTitle(state, { payload }) {
      const { selectedNodeId, optionId, optionTitle } = payload;
      let { options } = state.selectedNode.data;

      options = options?.map((option) => {
        if (option.id == optionId) return { ...option, title: optionTitle };
        return option;
      });

      state.selectedNode = {
        ...state.selectedNode,
        data: { ...state.selectedNode.data, options },
      };
      let updatedNode = null;
      state.nodes = state.nodes?.map((node) => {
        if (node.id == selectedNodeId) {
          updatedNode = {
            ...node,
            data: {
              ...node.data,
              options: node.data.options?.map((option) => {
                if (option.id == optionId) {
                  return { ...option, title: optionTitle };
                }
                return option;
              }),
            },
          };

          return updatedNode;
        }
        return node;
      });

      const findEdge = state.edges.find((edge) => edge.optionId == optionId);

      state.edges = state.edges?.map((edge) => {
        if (edge.optionId == optionId) {
          return { ...edge, label: optionTitle };
        }
        return edge;
      });

      if (state.updatedEdgeChache?.length == 0 && findEdge) {
        state.updatedEdgeChache = [findEdge];
      } else {
        const isAlreadyInUpdatedEdgeCache = state.updatedEdgeChache?.find(
          (edge) => edge?.id == findEdge?.id,
        );
        if (!isAlreadyInUpdatedEdgeCache && findEdge) {
          state.updatedEdgeChache = [...state.updatedEdgeChache, findEdge];
        }
      }
    },
    updatePostbackText(state, { payload }) {
      const { selectedNodeId, optionId, targetNodeId } = payload;
      let updatedNode = null;
      state.nodes = state.nodes?.map((node) => {
        if (node.id == selectedNodeId) {
          updatedNode = {
            ...node,
            data: {
              ...node.data,
              options: node.data.options?.map((option) => {
                if (option.id == optionId) {
                  return { ...option, postbackText: `stepId=${targetNodeId}` };
                }
                return option;
              }),
            },
          };

          return updatedNode;
        }
        return node;
      });

      if (updatedNode) {
        state.selectedNode = updatedNode;
      }
    },
    deleteOption(state, { payload }) {
      const { nodeId, optionId } = payload;
      const currNodeOptions = JSON.parse(JSON.stringify([...state.selectedNode.data.options]));
      const updateOptions = currNodeOptions?.filter((opt) => opt.id != optionId);

      const updatedCurrNode = {
        ...state.selectedNode,
        data: {
          ...state.selectedNode.data,
          options: [...updateOptions],
        },
      };
      state.selectedNode = updatedCurrNode;

      state.nodes = state.nodes?.map((node) => {
        if (node.id == nodeId) {
          return updatedCurrNode;
        }
        return node;
      });
      const deletedEdge = state.edges.find((edge) => edge.optionId == optionId);
      state.edges = state.edges?.filter((edge) => edge.optionId !== optionId);
      if (deletedEdge) {
        state.updatedEdgeChache = state.updatedEdgeChache?.filter((edge) => edge.id !== deletedEdge.id);
        state.deletedOptionNodeEdgeCache = [...state.deletedOptionNodeEdgeCache, deletedEdge];
      }
    },
    resetCanvas(state) {
      state.nodes = [];
      state.edges = [];
      state.selectedNode = null;
      state.rootStepId = null;
      state.botFlowExists = false;
      state.configScreenOpen = false;
      state.editNodeCache = null;
      state.isEditable = true;
      state.deletedOptionNodeEdgeCache = [];
      state.updatedEdgeChache = [];
    },
    changeBotMode(state, { payload }) {
      state.mode = payload;
    },
    resetBotFlowList(state) {
      state.botFlows = [];
    },
    changeListNode(state, action) {
      state.listNode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addBotFlowSteps.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addBotFlowSteps.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(addBotFlowSteps.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getBotFlow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getBotFlow.fulfilled, (state, action) => {
      const nodes = getSchemaNodes(action.payload);
      const edges = getSchemaEdges(action.payload);

      state.nodes = nodes;
      state.edges = edges;
      state.rootStepId = getSchemaRootStepId(action.payload);
      state.botFlowExists = !action.payload?._schemaNotFound;
      state.loading = false;
      state.isEditable = true;
    });
    builder.addCase(getBotFlow.rejected, (state) => {
      state.loading = false;
      state.botFlowExists = false;
    });

    builder.addCase(addNewBotFlow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addNewBotFlow.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.botFlows = [payload, ...state.botFlows];
      state.totalbotflowCount += 1;
    });
    builder.addCase(addNewBotFlow.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBotFlow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateBotFlow.fulfilled, (state, action) => {
      const fallbackPayload = action.meta.arg?.botFlowData || {};
      const responsePayload = getSchemaPayload(action.payload);

      state.nodes = Array.isArray(responsePayload?.nodes)
        ? responsePayload.nodes
        : Array.isArray(fallbackPayload?.nodes)
        ? fallbackPayload.nodes
        : [];
      state.edges = Array.isArray(responsePayload?.edges)
        ? responsePayload.edges
        : Array.isArray(fallbackPayload?.edges)
        ? fallbackPayload.edges
        : [];
      state.rootStepId =
        responsePayload?.rootStepId !== undefined
          ? responsePayload.rootStepId
          : fallbackPayload?.rootStepId || null;
      state.loading = false;
      state.configScreenOpen = false;
      state.botFlowExists = true;
    });
    builder.addCase(updateBotFlow.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBotflowDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateBotflowDetails.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.botFlows = state.botFlows?.map((item) => {
        if (item.id == payload.id) {
          return payload;
        }
        return item;
      });
    });
    builder.addCase(updateBotflowDetails.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(deleteBotflow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteBotflow.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.botFlows = state.botFlows?.filter((item) => item.id != payload.id);
      state.totalbotflowCount = Math.max(0, (state.totalbotflowCount || 0) - 1);
    });
    builder.addCase(deleteBotflow.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(getAllBotFlow.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllBotFlow.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.botFlows = payload?.rows || [];
      state.totalbotflowCount = payload?.count || 0;
    });
    builder.addCase(getAllBotFlow.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const {
  changeBotflowPage,
  changeBotflowLimit,
  changeSelectectBotFlow,
  setNodes,
  setEdges,
  addNodeToStore,
  addEdgeToStore,
  changeSelectedNode,
  changeQuickReplyNode,
  updateTextNode,
  deleteNode,
  deleteConnectedNodes,
  deleteEdge,
  layoutEdges,
  layoutNodes,
  changeRootStepId,
  changeListNode,
  deleteNodeData,
  resetCanvas,
  addNewOption,
  updatePostbackText,
  updateNodePayload,
  updateOptionTitle,
  updateNodeData,
  deleteOption,
  openConfigScreen,
  closeConfigScreen,
  undoNodeData,
  changeBotMode,
  resetBotFlowList,
} = wcaBotFlowSlice.actions;

export {
  addBotFlowSteps,
  addNewBotFlow,
  deleteBotflow,
  getAllBotFlow,
  getBotFlow,
  updateBotFlow,
  updateBotflowDetails,
} from './botflow-action';

export default wcaBotFlowSlice.reducer;